export const ATTENDEE_NAMES_COUNT_MISMATCH_ERROR = 'common.errors.attendeeNamesCountMismatch'
export const ATTENDEE_NAME_REQUIRED_ERROR = 'common.errors.attendeeNameRequired'
export const ATTENDEE_EMAIL_INVALID_ERROR = 'common.errors.attendeeEmailInvalid'

export const MAX_TICKETS_PER_PURCHASE = 50

export const COMPLIMENTARY_TICKET_TYPE = { id: 'complimentary', name: 'Tiszteletjegy' } as const

export const MAX_ATTENDEE_NAME_LENGTH = 200
export const MAX_ATTENDEE_EMAIL_LENGTH = 320

/**
 * Deliberately loose: the address is optional and only decides whether one extra email is sent, so
 * this is here to catch a typo, not to adjudicate RFC 5322.
 *
 * Shared rather than restated per app. The server refuses a bad list and the three forms grey out
 * their own submit button on the same grounds — two implementations of one rule is how they come to
 * disagree, and a form that accepts what the server rejects is a dead end for whoever is filling it.
 */
export const ATTENDEE_EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const isAttendeeEmailShaped = (email: string): boolean =>
    ATTENDEE_EMAIL_SHAPE.test(email.trim())

export type ResolvedAttendee = {
    name: string
    /** Where this one ticket is sent on its own. Absent when no address was given for it. */
    email: string | null
}

/**
 * The one rule all three purchase paths answer to, so the app, the web checkout and the
 * application form cannot disagree about what a valid attendee list looks like.
 *
 * The buyer's own name stands behind the first ticket, which is why only tickets 2..N are
 * required — a single-ticket purchase stays a one-click flow even with the field left untouched,
 * and typing over it is how that ticket goes to someone else. An address is optional on every
 * ticket; giving one only adds a personal copy of that ticket.
 */
export const findAttendeeNamesError = ({
    names,
    emails,
    quantity,
}: {
    names: readonly string[] | null | undefined
    emails?: readonly string[] | null
    quantity: number
}): string | null => {
    const given = names ?? []

    // An absent list is how a client that predates this field, or a one-ticket purchase that never
    // rendered the inputs, arrives. Both fall back to the buyer.
    if (given.length === 0) {
        if (quantity > 1) return ATTENDEE_NAMES_COUNT_MISMATCH_ERROR
    } else {
        if (given.length !== quantity) return ATTENDEE_NAMES_COUNT_MISMATCH_ERROR
        if (given.slice(1).some((name) => name.trim() === '')) return ATTENDEE_NAME_REQUIRED_ERROR
    }

    const addresses = emails ?? []

    if (addresses.length > quantity) return ATTENDEE_NAMES_COUNT_MISMATCH_ERROR

    // Blank means "no personal copy", so only a filled-in address is checked.
    return addresses.some((email) => email.trim() !== '' && !isAttendeeEmailShaped(email))
        ? ATTENDEE_EMAIL_INVALID_ERROR
        : null
}

/**
 * The attendees the tickets are actually stamped with: trimmed, padded to `quantity`, with the
 * buyer standing in wherever no name was typed. A name is never blank, so a door always has one to
 * read out; an address stays null unless it was given.
 */
export const resolveAttendees = ({
    names,
    emails,
    quantity,
    buyerName,
}: {
    names: readonly string[] | null | undefined
    emails?: readonly string[] | null
    quantity: number
    buyerName: string
}): ResolvedAttendee[] =>
    Array.from({ length: Math.max(quantity, 1) }, (_, index) => ({
        name: names?.[index]?.trim() || buyerName.trim(),
        email: emails?.[index]?.trim() || null,
    }))

/**
 * Which tickets a form still has to be told about: the indexes whose name is required and empty,
 * and the indexes whose address was typed but is not one. What `findAttendeeNamesError` answers in
 * one message, a form needs per field so it can mark the offending row.
 */
export const findAttendeeFieldErrors = ({
    names,
    emails,
    quantity,
}: {
    names: readonly string[]
    emails?: readonly string[]
    quantity: number
}): { missingNameIndexes: number[]; invalidEmailIndexes: number[] } => {
    const indexes = Array.from({ length: Math.max(quantity, 0) }, (_, index) => index)

    return {
        missingNameIndexes: indexes.filter(
            (index) => index > 0 && (names[index] ?? '').trim() === '',
        ),
        invalidEmailIndexes: indexes.filter((index) => {
            const email = (emails?.[index] ?? '').trim()

            return email !== '' && !isAttendeeEmailShaped(email)
        }),
    }
}
